<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="400px"
      transition="dialog-transition"
    >
      <v-sheet>
        <v-toolbar dark class="primary">
          <v-btn
            icon
            @click="onClose"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Pago</v-toolbar-title>
        </v-toolbar>
        <v-container>
          <date-input
            v-model="fecha"
            label="Fecha"
            :rules="rules.fecha"
            max-today
          ></date-input>
          <month-picker
            v-model="periodo"
            readonly
          ></month-picker>
          <v-divider class="my-3"></v-divider>

          <!-- Listado Dinámico de Pagos -->
          <div v-for="(pago, index) in pagos" :key="index" class="d-flex align-center my-2">
            <v-row dense align="center">
              <v-col cols="6" class="py-0">
                <formas-pagos-select
                  v-model="pago.forma_pago_id"
                  :disabled="isSubmitting"
                  :exclude-ids="getFilteredFormasPagoIds(index)"
                ></formas-pagos-select>
              </v-col>
              <v-col cols="5" class="py-0">
                <importe-input
                  :value="pago.importe"
                  :disabled="!(precioId > 0) || isSubmitting"
                  @input="val => onInputPago(index, val)"
                  @enter="onSubmit"
                ></importe-input>
              </v-col>
              <v-col cols="1" class="py-0 text-right">
                <v-btn
                  icon
                  small
                  color="error"
                  v-if="pagos.length > 1"
                  @click="removePago(index)"
                  :disabled="isSubmitting"
                  class="mt-1"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- Botón para agregar forma de pago -->
          <div class="mb-4">
            <v-btn
              text
              small
              color="primary"
              @click="addPago"
              :disabled="!(precioId > 0) || isSubmitting || isAddPagoDisabled"
            >
              <v-icon left>mdi-plus</v-icon>
              Agregar Forma de Pago
            </v-btn>
          </div>

          <v-divider class="my-3"></v-divider>
          <v-text-field
            v-if="hasEfectivo"
            v-model.number="cambio"
            :disabled="isSubmitting"
            label="Vuelto / Cambio"
            color="primary"
            prepend-icon="mdi-cash-refund"
            type="number"
            min="0"
          ></v-text-field>
          <v-text-field
            v-model="glosa"
            :disabled="!(precioId > 0)"
            label="Observaciones"
            color="primary"
            prepend-icon="mdi-text"
            @keydown.enter="onSubmit"
          ></v-text-field>
          <v-footer>
            <div>$ {{ totalImporte | formatToTwoDecimals }}</div>
            <v-btn
              :disabled="disabledSubmit"
              color="primary"
              @click="onSubmit"
              :loading="loading"
              block
            >
              Registrar
            </v-btn>
          </v-footer>
        </v-container>
      </v-sheet>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import MonthPicker from '@/components/MonthPicker'
import DateInput from '@/components/DateInput'
import ImporteInput from '@/components/ImporteInput'
import FormasPagosSelect from '@/pages/formas_pagos/SelectComponent'
import PrecioMixin from '@/mixins/Precios';
import PeriodoMixin from '@/mixins/Periodo';
import { printReceiptPDF } from './pdf_recibo.js';
export default {
  name: 'CreditosPlenoCreateComponent',
  mixins: [PeriodoMixin, PrecioMixin],
  components: {
    'date-input': DateInput,
    'month-picker': MonthPicker,
    'formas-pagos-select': FormasPagosSelect,
    'importe-input': ImporteInput
  },
  model: {
    prop: 'selected',
    event: 'change'
  },
  props: {
    selected: Boolean
  },
  data () {
    return {
      hoy: null,
      focusGas: false,
      isSubmitting: false,
      cantidad: null,
      precio: 0,
      valid: false,
    }
  },
  beforeDestroy () {
    // Remueve el event listener en el gancho beforeDestroy
    this.onLimpiar()
  },
  mounted () {
    this.cuentaId = this.selectedId
    this.hoy = this.$moment().format('YYYY-MM-DD')
  },
  computed: {
    ...mapGetters('Cuentas', ['selectedId']),
    ...mapGetters('Creditos', ['edited', 'rules', 'loading', 'updatable']),
    dialog: {
      get () {
        return this.selected
      },
      set (value) {
        this.$emit('change', value)
      }
    },
    fecha: {
      get () {
        return this.edited.fecha
      },
      set (value) {
        this.setEdited({
          ...this.edited,
          fecha: value
        })
      }
    },
    periodo: {
      get () {
        return this.edited.periodo
      },
      set (value) {
        this.setEdited({
          ...this.edited,
          periodo: value
        })
      }
    },
    importe: {
      get () {
        return this.edited.importe
      },
      set (value) {
        const aux = value ? parseFloat(value) : 0
        this.setEdited({
          ...this.edited,
          importe: aux
        })
      }
    },
    glosa: {
      get () {
        return this.edited.glosa
      },
      set (value) {
        this.setEdited({
          ...this.edited,
          glosa: value
        })
      }
    },
    cuentaId: {
      get () {
        return this.edited.cuenta_id
      },
      set (value) {
        this.setEdited({
          ...this.edited,
          cuenta_id: value
        })
      }
    },
    precioId: {
      get () {
        return this.edited.precio_id
      },
      set (value) {
        this.setEdited({
          ...this.edited,
          precio_id: value
        })
      }
    },
    formaPagoId: {
      get () {
        return this.edited.forma_pago_id
      },
      set (value) {
        this.setEdited({
          ...this.edited,
          forma_pago_id: value
        })
      }
    },
    disabledSubmit () {
      return !this.updatable && !this.$can('creditos_pleno.store')
    },
    pagos: {
      get () {
        return this.edited.pagos || []
      },
      set (value) {
        this.setEdited({
          ...this.edited,
          pagos: value,
          forma_pago_id: value[0]?.forma_pago_id || null,
          importe: value.reduce((sum, p) => sum + (parseFloat(p.importe) || 0), 0)
        })
      }
    },
    totalImporte () {
      if (!this.pagos) return 0;
      return this.pagos.reduce((sum, p) => sum + (parseFloat(p.importe) || 0), 0);
    },
    isAddPagoDisabled () {
      const allFormas = this.$store.getters['FormasPagos/all'] || [];
      return this.pagos.length >= allFormas.length;
    },
    hasEfectivo () {
      return this.pagos && this.pagos.some(p => p.forma_pago_id === 1);
    },
    cambio: {
      get () {
        return this.edited.cambio || 0
      },
      set (value) {
        const val = value ? parseFloat(value) : 0
        if ((this.edited.cambio || 0) !== val) {
          this.setEdited({
            ...this.edited,
            cambio: val
          })
        }
      }
    },
    cuenta () {
      return this.$store.getters['Cuentas/selected']
    }
  },
  watch: {
    async dialog (newVal) {
      this.onLimpiar();
    },
    async fecha (newValue) {
      if (newValue) {
        this.periodo = await this.periodoFechaTo(newValue);
        const precio = await this.precioByVigencia(newValue);
        if(precio && precio.id > 0) {
          this.precioId = precio.id;
          this.precio = precio.pleno;
        }
      } else {
        this.precio = 0;
        this.periodo = null;
        this.precioId = null;
      }
      this.pagos = this.pagos.map(p => ({ ...p, importe: 0 }));
    },
    selectedId (newValue) {
      this.cuentaId = newValue
    },
    pagos: {
      handler (newPagos) {
        if (newPagos && !newPagos.some(p => p.forma_pago_id === 1)) {
          if (this.cambio !== 0) {
            this.cambio = 0;
          }
        }
      },
      deep: true
    },
  },
  methods: {
    ...mapActions('Creditos', ['onSave', 'onClearEdited', 'onClearSelected']),
    ...mapMutations('Creditos', ['setEdited']),
    async onLimpiar () {
      this.onClearEdited()
      this.onClearSelected()
      this.cuentaId = await this.selectedId;
      this.fecha = await this.$moment().format('YYYY-MM-DD');
      this.pagos = [{ forma_pago_id: 1, importe: 0 }];
      this.cantidad = null;
    },
    onSubmit(event) {
      event.preventDefault();
      // Si ya estamos enviando, salimos de la función
      if (this.isSubmitting || !this.fecha) {
        return;
      }
      this.isSubmitting = true; // Establecer la bandera en true

      this.onSave().then(response => {
        const transaccion = response.data;
        const cuenta = this.cuenta;

        this.onLimpiar();
        this.$store.dispatch('Transacciones/onFetch');
        this.$store.dispatch('Cuentas/onRefresh');
        this.dialog = false;

        this.$nextTick(() => {
          try {
            printReceiptPDF(transaccion, cuenta);
          } catch (error) {
            console.error('Error al generar el PDF del recibo:', error);
            alert('El pago se registró correctamente, pero hubo un problema al generar el ticket de impresión. Puede reimprimirlo desde el historial de transacciones.');
          }
        });
      })
      .finally(() => {
        this.isSubmitting = false; // Restablecer la bandera a false al finalizar
      });
    },
    handleInputCantidad(newVal) {
      const cantidad = parseFloat(newVal);
      if (cantidad > 0) {
        this.importe = parseFloat(cantidad * this.precio);
      }
    },
    onClose (event) {
      event.preventDefault()
      this.dialog = false
    },
    onInput(newVal) {
      this.importe = newVal;
      this.cantidad = null;
    },
    addPago() {
      const selectedIds = this.pagos.map(p => p.forma_pago_id);
      const allFormas = this.$store.getters['FormasPagos/all'] || [];
      const nextForma = allFormas.find(f => !selectedIds.includes(f.id));
      const nextId = nextForma ? nextForma.id : (allFormas[0]?.id || 1);

      const newPagos = [...this.pagos, { forma_pago_id: nextId, importe: 0 }];
      this.pagos = newPagos;
    },
    removePago(index) {
      if (this.pagos.length <= 1) return;
      const newPagos = this.pagos.filter((_, i) => i !== index);
      this.pagos = newPagos;
    },
    onInputPago(index, newVal) {
      const newPagos = this.pagos.map((p, i) => {
        if (i === index) {
          return { ...p, importe: newVal ? parseFloat(newVal) : 0 };
        }
        return p;
      });
      this.pagos = newPagos;
    },
    getFilteredFormasPagoIds(index) {
      return this.pagos
        .filter((_, i) => i !== index)
        .map(p => p.forma_pago_id);
    }
  },
}
</script>
